import { useEffect, useRef, useState, useCallback } from "react";
import io from "socket.io-client";
import config from "../../configs/config";

/**
 * Custom hook for WebSocket collaboration in survey builder
 * @param {Object} params - Configuration parameters
 * @param {string} params.projectId - Current project ID
 * @param {string} params.userId - Current user ID
 * @param {string} params.username - Current username
 * @param {Function} params.onPropertyUpdate - Callback when property updates
 * @param {Function} params.onRowUpdate - Callback when row updates
 * @param {Function} params.onRowAdd - Callback when row added
 * @param {Function} params.onRowDelete - Callback when row deleted
 * @param {Function} params.onBulkRowsAdd - Callback when bulk rows added
 * @param {Function} params.onQuestionDelete - Callback when question deleted
 * @param {Function} params.onQuestionCreate - Callback when question created
 * @param {Function} params.onUserJoin - Callback when user joins
 * @param {Function} params.onUserLeave - Callback when user leaves
 * @param {Function} params.onQuestionLock - Callback when question locked
 * @param {Function} params.onQuestionUnlock - Callback when question unlocked
 */
export const useSurveyWebSocket = ({
  projectId,
  userEmail, // Changed from userId to userEmail
  username,
  onPropertyUpdate,
  onRowUpdate,
  onRowAdd,
  onRowDelete,
  onBulkRowsAdd,
  onQuestionDelete,
  onQuestionCreate,
  onUserJoin,
  onUserLeave,
  onQuestionLock,
  onQuestionUnlock,
}) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [questionLocks, setQuestionLocks] = useState({});
  const [connectionError, setConnectionError] = useState(null);

  // Flag to prevent echoing our own changes
  const isLocalChange = useRef(false);

  // Debounce timers
  const debounceTimers = useRef({});

  // Heartbeat interval
  const heartbeatInterval = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    if (!projectId || !userEmail) return;

    // Create socket connection with email authentication
    const socket = io(`${config.WEBSOCKET_URL || config.API_BASE_URL}/survey`, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      query: {
        email: userEmail, // Pass email in query for authentication
      },
    });

    socketRef.current = socket;

    // Connection handlers
    socket.on("connect", () => {
      // console.log ("✅ WebSocket connected");
      setIsConnected(true);
      setConnectionError(null);

      // Join project room
      socket.emit("join_project", {
        projectId,
        userEmail, // Using email as unique identifier
        username,
      });

      // Start heartbeat
      startHeartbeat();
    });

    socket.on("disconnect", (reason) => {
      // console.log ("❌ WebSocket disconnected:", reason);
      setIsConnected(false);
      stopHeartbeat();
    });

    socket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      setConnectionError(error.message);
    });

    // Project join/leave handlers
    socket.on("joined_project", (data) => {
      // console.log ("Joined project:", data);
      setActiveUsers(data.activeUsers || []);
      setQuestionLocks(data.questionLocks || {});
    });

    socket.on("user_joined", (data) => {
      // console.log ("User joined:", data.username);
      setActiveUsers(data.activeUsers || []);
      if (onUserJoin) onUserJoin(data);
    });

    socket.on("user_left", (data) => {
      // console.log ("User left:", data.username);
      setActiveUsers(data.activeUsers || []);
      if (onUserLeave) onUserLeave(data);
    });

    // Question lock handlers
    socket.on("question_locked", (data) => {
      // console.log ("Question locked:", data.questionKey, "by", data.username);
      setQuestionLocks((prev) => ({
        ...prev,
        [data.questionKey]: {
          userId: data.userId,
          username: data.username,
        },
      }));
      if (onQuestionLock) onQuestionLock(data);
    });

    socket.on("question_unlocked", (data) => {
      // console.log ("Question unlocked:", data.questionKey);
      setQuestionLocks((prev) => {
        const newLocks = { ...prev };
        delete newLocks[data.questionKey];
        return newLocks;
      });
      if (onQuestionUnlock) onQuestionUnlock(data);
    });

    // Property update handler
    socket.on("property_updated", (data) => {
      // console.log ("Property updated:", data);
      if (onPropertyUpdate && data.userEmail !== userEmail) {
        onPropertyUpdate(data);
      }
    });

    // Row change handlers
    socket.on("row_created", (data) => {
      // console.log ("Row created:", data);
      if (onRowAdd && data.userEmail !== userEmail) {
        onRowAdd(data);
      }
    });

    socket.on("row_changed", (data) => {
      // console.log ("Row changed:", data);
      if (onRowUpdate && data.userEmail !== userEmail) {
        onRowUpdate(data);
      }
    });

    socket.on("row_removed", (data) => {
      // console.log ("Row removed:", data);
      if (onRowDelete && data.userEmail !== userEmail) {
        onRowDelete(data);
      }
    });

    socket.on("bulk_rows_created", (data) => {
      // console.log ("Bulk rows created:", data);
      if (onBulkRowsAdd && data.userEmail !== userEmail) {
        onBulkRowsAdd(data);
      }
    });

    // Question CRUD handlers
    socket.on("question_removed", (data) => {
      // console.log ("Question removed:", data);
      if (onQuestionDelete && data.userEmail !== userEmail) {
        onQuestionDelete(data);
      }
    });

    socket.on("question_added", (data) => {
      // console.log ("Question added:", data);
      if (onQuestionCreate && data.userEmail !== userEmail) {
        onQuestionCreate(data);
      }
    });

    // Cleanup
    return () => {
      stopHeartbeat();
      if (socket.connected) {
        socket.emit("leave_project", { projectId, userEmail });
        socket.disconnect();
      }
    };
  }, [projectId, userEmail, username]);

  // Heartbeat functions
  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatInterval.current = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("heartbeat", { projectId, userEmail });
      }
    }, 30000); // Every 30 seconds
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  };

  // Debounce helper
  const debounce = (key, fn, delay = 500) => {
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }
    debounceTimers.current[key] = setTimeout(fn, delay);
  };

  // Emit functions
  const emitPropertyChange = useCallback(
    (questionKey, property, value, previousValue) => {
      if (!socketRef.current?.connected) return;

      debounce(
        `property_${questionKey}_${property}`,
        () => {
          socketRef.current.emit("property_changed", {
            projectId,
            userEmail,
            username,
            questionKey,
            property,
            value,
            previousValue,
          });
        },
        500
      );
    },
    [projectId, userEmail, username]
  );

  const emitRowAdd = useCallback(
    (questionKey, rowData, rowType, position) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("row_added", {
        projectId,
        userEmail,
        username,
        questionKey,
        rowData,
        rowType,
        position,
      });
    },
    [projectId, userEmail, username]
  );

  const emitRowUpdate = useCallback(
    (questionKey, rowIndex, rowType, field, value, previousValue) => {
      if (!socketRef.current?.connected) return;

      debounce(
        `row_${questionKey}_${rowIndex}_${field}`,
        () => {
          socketRef.current.emit("row_updated", {
            projectId,
            userEmail,
            username,
            questionKey,
            rowIndex,
            rowType,
            field,
            value,
            previousValue,
          });
        },
        500
      );
    },
    [projectId, userEmail, username]
  );

  const emitRowDelete = useCallback(
    (questionKey, rowIndex, rowType, rowData) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("row_deleted", {
        projectId,
        userEmail,
        username,
        questionKey,
        rowIndex,
        rowType,
        rowData,
      });
    },
    [projectId, userEmail, username]
  );

  const emitBulkRowsAdd = useCallback(
    (questionKey, rowsData, rowType, mode) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("bulk_rows_added", {
        projectId,
        userEmail,
        username,
        questionKey,
        rowsData,
        rowType,
        mode,
      });
    },
    [projectId, userEmail, username]
  );

  const emitQuestionDelete = useCallback(
    (questionKey, questionData) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("question_deleted", {
        projectId,
        userEmail,
        username,
        questionKey,
        questionData,
      });
    },
    [projectId, userEmail, username]
  );

  const emitQuestionCreate = useCallback(
    (questionKey, questionData) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("question_created", {
        projectId,
        userEmail,
        username,
        questionKey,
        questionData,
      });
    },
    [projectId, userEmail, username]
  );

  const emitEditingStart = useCallback(
    (questionKey) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("editing_start", {
        projectId,
        userEmail,
        username,
        questionKey,
      });
    },
    [projectId, userEmail, username]
  );

  const emitEditingEnd = useCallback(
    (questionKey) => {
      if (!socketRef.current?.connected) return;

      socketRef.current.emit("editing_end", {
        projectId,
        userEmail,
        username,
        questionKey,
      });
    },
    [projectId, userEmail, username]
  );

  const requestSync = useCallback(() => {
    if (!socketRef.current?.connected) return;

    socketRef.current.emit("request_sync", {
      projectId,
      userEmail,
    });
  }, [projectId, userEmail]);

  return {
    isConnected,
    activeUsers,
    questionLocks,
    connectionError,
    emitPropertyChange,
    emitRowAdd,
    emitRowUpdate,
    emitRowDelete,
    emitBulkRowsAdd,
    emitQuestionDelete,
    emitQuestionCreate,
    emitEditingStart,
    emitEditingEnd,
    requestSync,
  };
};
